// src/components/store/sections/product-cards/useCardActions.ts
"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useCart } from "@/context/CartContext";

export interface UniversalProductItem {
  id: number | string;
  name?: string;
  title?: string;
  slug?: string;
  price?: number | string;
  sale_price?: number | string;
  discount_price?: number | string;
  regular_price?: number | string;
  compare_price?: number | string | null;
  image?: string | null;
  primary_image?: string | null;
  thumbnail?: string | null;
  images?: any[];
  category_name?: string;
  description?: string;
  rating?: number | string;
  unit?: string;
  stock?: number;
  stock_quantity?: number;
  deal_end_time?: string | null;
}

export interface UniversalProductCardProps {
  product: UniversalProductItem;
  index?: number;
  subdomain?: string;
  primaryColor?: string;
  showTimer?: boolean;
}

export function useCardActions(
  product: UniversalProductItem,
  subdomain: string = "nextlook",
  fallbackImg: string = "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=600&auto=format&fit=crop&q=80"
) {
  const router = useRouter();
  let cartContext: any = null;
  try {
    cartContext = useCart?.();
  } catch {}

  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [buying, setBuying] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);
  const [quantity, setQuantity] = useState(1);

  // Live countdown timer state for Deal section / urgency badges
  const [timer, setTimer] = useState({ hours: 12, minutes: 45, seconds: 30 });

  useEffect(() => {
    const seed = Number(String(product?.id || 1).replace(/\D/g, "")) || 1;
    let totalSec = ((seed * 37) % 10 + 4) * 3600 + ((seed * 19) % 50 + 5) * 60 + ((seed * 13) % 59);

    const updateTimer = () => {
      totalSec = totalSec > 0 ? totalSec - 1 : 43200;
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      setTimer({ hours: h, minutes: m, seconds: s });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [product?.id]);

  const resolveImageUrl = useCallback(
    (rawInput?: string | null) => {
      const raw =
        rawInput ||
        product?.primary_image ||
        product?.image ||
        (Array.isArray(product?.images) && product.images[0]?.image) ||
        (Array.isArray(product?.images) && typeof product.images[0] === "string"
          ? product.images[0]
          : null) ||
        product?.thumbnail;

      if (!raw) return fallbackImg;
      const path = String(raw).trim();
      if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) {
        return path;
      }
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");
      const base = isLocal ? `http://${subdomain}.localhost:8000` : "http://127.0.0.1:8000";
      return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
    },
    [product, subdomain, fallbackImg]
  );

  const productImg = resolveImageUrl();
  const productName = product?.name || product?.title || "Premium Product";
  const productSlug = product?.slug || product?.id || "1";
  const productUrl = `/products/${productSlug}`;
  const categoryName = product?.category_name || "Featured Pick";
  const description =
    product?.description || "Authentic quality curated item with fast doorstep delivery.";
  const rating = Number(product?.rating || 4.9);
  const stockCount = Number(product?.stock_quantity ?? product?.stock ?? 18);

  const currentPrice = Number(
    product?.discount_price || product?.sale_price || product?.price || 850
  );
  const comparePrice = Number(
    product?.compare_price || product?.regular_price || Math.round(currentPrice * 1.3)
  );
  const discountPercent =
    comparePrice > currentPrice && currentPrice > 0
      ? Math.round(((comparePrice - currentPrice) / comparePrice) * 100)
      : 25;

  const handleAddToCart = (e: React.MouseEvent, customQty?: number) => {
    e.preventDefault();
    e.stopPropagation();
    const qtyToUse = customQty || quantity || 1;
    setAdding(true);
    setAdded(true);

    const itemObj = {
      id: product.id,
      product_id: product.id,
      name: productName,
      price: currentPrice,
      image: productImg,
      quantity: qtyToUse,
      unit: product.unit || "1 pc",
    };

    try {
      const storageKeys = [
        "cart",
        "cart_items",
        `cart_${subdomain}`,
        `xylo_cart_${subdomain}`,
        `soft_cart_${subdomain}`,
        "soft_cart",
        "xylo_cart",
      ];
      storageKeys.forEach((key) => {
        try {
          const raw = localStorage.getItem(key);
          let list: any[] = raw ? JSON.parse(raw) : [];
          if (!Array.isArray(list)) list = [];
          const idx = list.findIndex((x: any) => String(x.id) === String(product.id));
          if (idx > -1) {
            list[idx].quantity = (Number(list[idx].quantity) || 1) + qtyToUse;
          } else {
            list.push({ ...itemObj });
          }
          localStorage.setItem(key, JSON.stringify(list));
        } catch {}
      });

      if (cartContext && typeof cartContext.addToCart === "function") {
        cartContext.addToCart(product, qtyToUse);
        if (typeof cartContext.openCart === "function") cartContext.openCart();
      }
      window.dispatchEvent(new CustomEvent("addToCart", { detail: itemObj }));
      window.dispatchEvent(new Event("cartUpdated"));
      window.dispatchEvent(new Event("storage"));
      window.dispatchEvent(new CustomEvent("openCartDrawer"));
    } catch (err) {
      console.error("Add to cart error:", err);
    }

    setTimeout(() => setAdding(false), 500);
    setTimeout(() => setAdded(false), 1800);
  };

  const handleBuyNow = (e: React.MouseEvent, customQty?: number) => {
    e.preventDefault();
    e.stopPropagation();
    const qtyToUse = customQty || quantity || 1;
    setBuying(true);

    const singleItemObj = {
      id: product.id,
      product_id: product.id,
      name: productName,
      title: productName,
      price: currentPrice,
      image: productImg,
      quantity: qtyToUse,
      unit: product.unit || "1 pc",
    };

    try {
      sessionStorage.setItem("direct_checkout_item", JSON.stringify(singleItemObj));
      const storageKeys = [
        "cart",
        "cart_items",
        `cart_${subdomain}`,
        `xylo_cart_${subdomain}`,
        `soft_cart_${subdomain}`,
        "soft_cart",
        "xylo_cart",
      ];
      storageKeys.forEach((key) => {
        try {
          localStorage.setItem(key, JSON.stringify([singleItemObj]));
        } catch {}
      });
      window.dispatchEvent(new Event("cartUpdated"));
      window.dispatchEvent(new Event("storage"));
    } catch {}

    router.push(`/checkout?buy_now=true&product_id=${product.id}`);
  };

  return {
    productImg,
    productName,
    productSlug,
    productUrl,
    categoryName,
    description,
    rating,
    stockCount,
    currentPrice,
    comparePrice,
    discountPercent,
    adding,
    added,
    buying,
    wishlisted,
    setWishlisted,
    quantity,
    setQuantity,
    timer,
    handleAddToCart,
    handleBuyNow,
  };
}
